import React from 'react';
import { resolvePlaceholderUrl } from '../services/ArtworkResolver';
import { resolveUploadAssetUrl } from '../utils/uploadAssetUrlResolver';
import { DEBUG_ENABLED } from '../utils/DebugLogger';

// @date_of_change: 2025-10-05
// @date_of_change: 2025-10-07 - Made fallback contextual: accept 'map', 'token', or explicit hint
// @date_of_change: 2025-10-09 - SIMPLIFIED: ArtworkResolver already handles all URL resolution
// This component now just displays whatever URL it receives, with fallback on error
export default function ImageWithFallback({ src, alt, fallback = 'token-default', ...rest }) {
  const [err, setErr] = React.useState(false);
  const [resolvedSrc, setResolvedSrc] = React.useState('');
  const debugLog = React.useCallback((...args) => {
    if (DEBUG_ENABLED) {
      console.debug(...args);
    }
  }, []);

  // Simple passthrough - ArtworkResolver has already resolved the URL
  React.useEffect(() => {
    setErr(false);

    // If no src provided, show placeholder immediately
    if (!src) {
      const placeholderUrl = resolvePlaceholderUrl(fallback);
      debugLog('[ImageWithFallback] No src, using placeholder:', placeholderUrl);
      setResolvedSrc(placeholderUrl);
      return;
    }

    // Pass through the src as-is (already resolved by ArtworkResolver)
    const normalizedSrc = resolveUploadAssetUrl(src);
    debugLog('[ImageWithFallback] Received src:', normalizedSrc);
    setResolvedSrc(normalizedSrc);
  }, [src, fallback, debugLog]);

  // If an error occurs after we attempted the resolved URL, fall back to placeholder
  React.useEffect(() => {
    if (err) {
      const placeholderUrl = resolvePlaceholderUrl(fallback);
      debugLog('[ImageWithFallback] Image failed to load, using placeholder:', placeholderUrl, 'Original src was:', resolvedSrc);
      setResolvedSrc(placeholderUrl);
    }
  }, [err, fallback, resolvedSrc, debugLog]);

  // Only use crossOrigin for uploaded images (uploads/), not assets
  const needsCrossOrigin = resolvedSrc && typeof resolvedSrc === 'string' && resolvedSrc.includes('/uploads/');

  return (
    <img
      src={resolvedSrc}
      alt={alt || ''}
      onError={() => setErr(true)}
      loading="lazy"
      decoding="async"
      {...(needsCrossOrigin && { crossOrigin: 'anonymous' })}
      {...rest}
    />
  );
}
